function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(callback, limit) {
  var waiting = false; // Initially, we're not waiting
  return function () {
    // We return a throttled function
    if (!waiting) {
      // If we're not waiting
      callback.apply(this, arguments); // Execute users function
      waiting = true; // Prevent future invocations
      setTimeout(function () {
        // After a period of time
        waiting = false; // And allow future invocations
      }, limit);
    }
  };
}

/**
 * Wrapper around Object.assign that deletes null or undefined
 * keys from the provided object, making them fall back to
 * values in the defaults object.
 *
 *
 * @param {Object} defaults - An object with defaults for settings/config
 * @param {Object} provided - Provided settings/config object
 * @returns {Object} - Merged object
 */

function objectWithDefaults(defaults, provided) {
  filterObjectByValues(provided, (value) => {
    return value === null || value === undefined;
  });

  return Object.assign(defaults, provided);
}

function wrapAll(nodes, wrapper) {
  // Cache the current parent and previous sibling of the first node.
  var parent = nodes[0].parentNode;
  var previousSibling = nodes[0].previousSibling;

  // Place each node in wrapper.
  //  - If nodes is an array, we must increment the index we grab from
  //    after each loop.
  //  - If nodes is a NodeList, each node is automatically removed from
  //    the NodeList when it is removed from its parent with appendChild.
  for (var i = 0; nodes.length - i; wrapper.firstChild === nodes[0] && i++) {
    wrapper.appendChild(nodes[i]);
  }

  // Place the wrapper just after the cached previousSibling
  parent.insertBefore(wrapper, previousSibling.nextSibling);

  return wrapper;
}

function unwrap(wrapper) {
  // place childNodes in document fragment
  var docFrag = document.createDocumentFragment();
  while (wrapper.firstChild) {
    var child = wrapper.removeChild(wrapper.firstChild);
    docFrag.appendChild(child);
  }

  // replace wrapper with document fragment
  wrapper.parentNode.replaceChild(docFrag, wrapper);
}

function initTeleport(el) {
  if (!el) return;

  const teleportCandidates = el.querySelectorAll('[data-should-teleport]');

  if (teleportCandidates.length) {
    teleportCandidates.forEach((teleportCandidate) => {
      teleportCandidate.setAttribute(
        'x-teleport',
        teleportCandidate.dataset.shouldTeleport
      );
    });
  }
}

async function fetchSectionHTML(url, selector) {
  const res = await fetch(url);
  const fetchedSection = await res.text();
  const result = querySelectorInHTMLString(selector, fetchedSection);
  if (result === null) {
    return null;
  } else {
    return result.innerHTML;
  }
}

function fetchConfigDefaults(type = 'json') {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json;',
      Accept: `application/${type}`,
    },
  };
}

function parseDOMFromString(htmlString) {
  window.___shapesDOMParser = window.___shapesDOMParser || new DOMParser();

  return window.___shapesDOMParser.parseFromString(htmlString, 'text/html');
}

function querySelectorInHTMLString(selector, htmlString) {
  return parseDOMFromString(htmlString).querySelector(selector);
}

let touchDevice = false;

window.setTouch = function () {
  touchDevice = true;
};

window.isTouch = function () {
  return touchDevice;
};

function getModalLabel(modalSlotName, slotEl) {
  if (Alpine.store('modals')[modalSlotName].open) {
    const labelSourceEl = Array.from(slotEl.children).filter((el) =>
      el.hasAttribute('data-modal-label')
    )[0];

    if (labelSourceEl) {
      return labelSourceEl.dataset.modalLabel;
    }
  }

  return false;
}

// Keep drawer add/change requests using this helper and their rendering in order.
// A failed render may follow a successful add: recover with a GET, never replay the POST.
window.ThemeCart = (() => {
  const sections = 'cart-items,cart-footer,cart-item-count,cart-live-region';
  let queue = Promise.resolve();
  let recovering = false;
  let pending = 0;

  function notifyPending() {
    window.dispatchEvent(new CustomEvent('shapes:cart:pendingchange', {
      detail: { pending },
    }));
  }

  // Native checkout must wait for every queued add/change to finish.
  // This deliberately leaves accelerated checkout widgets and unrelated forms alone.
  document.addEventListener('submit', (event) => {
    if (!pending) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart' ||
        !form.closest('[data-cart-items]')) return;
    const submitter = event.submitter;
    const accelerated = '.additional-checkout-buttons, .shopify-payment-button, [data-shopify="payment-button"]';
    if (form.closest(accelerated) || (submitter && submitter.closest(accelerated))) return;
    if (submitter && submitter.name !== 'checkout') return;
    event.preventDefault();
    const message = 'Your cart is updating. Please wait, then select Checkout.';
    if (typeof window.liveRegion === 'function') window.liveRegion(message);
  }, true);

  function validate(data) {
    const html = data && data.sections;
    if (!html || typeof html !== 'object') throw new Error('Missing cart sections');
    const items = typeof html['cart-items'] === 'string' &&
      querySelectorInHTMLString('[data-cart-items]', html['cart-items']);
    const footer = typeof html['cart-footer'] === 'string' &&
      querySelectorInHTMLString('[data-cart-footer]', html['cart-footer']);
    const countText = typeof html['cart-item-count'] === 'string' &&
      parseDOMFromString(html['cart-item-count']).body.textContent.trim();
    if (!items || !footer || !/^\d+$/.test(countText || '')) {
      throw new Error('Incomplete cart sections');
    }
    const count = Number(countText);
    if (!Number.isSafeInteger(count)) throw new Error('Invalid cart count');
    const region = typeof html['cart-live-region'] === 'string'
      ? parseDOMFromString(html['cart-live-region']).body.textContent.trim()
      : '';
    return { items, footer, count, region };
  }

  function recover(error) {
    if (recovering) return;
    recovering = true;
    console.error('[cart] Could not refresh cart sections', error);
    window.location.assign((window.theme && theme.routes.cart_url) || '/cart');
  }

  function renderEvent(event, render, role) {
    let promise;
    try {
      const snapshot = event.detail.cartSnapshot || validate(event.detail.response);
      promise = Promise.resolve().then(() => render(snapshot));
    } catch (error) {
      promise = Promise.reject(error);
    }
    // Catch here as legacy event publishers do not await listeners.
    promise = promise.catch((error) => { recover(error); throw error; });
    if (event.detail.cartRenders) {
      event.detail.cartRenders.push(promise);
      event.detail.cartRenderers.push(role);
    }
    else promise.catch(() => {});
    return promise;
  }

  async function publish(data, eventName, extra = {}) {
    let cartSnapshot;
    try { cartSnapshot = validate(data); }
    catch (error) { recover(error); return; }
    const detail = { ...extra, response: data, cartSnapshot, cartRenders: [], cartRenderers: [] };
    document.body.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));
    if (!['items', 'footer', 'count'].every((role) => detail.cartRenderers.includes(role))) {
      recover(new Error('Cart renderers were not initialized'));
      await Promise.all(detail.cartRenders).catch(() => {});
      return;
    }
    // Listener failures already start the cart-page recovery.
    await Promise.all(detail.cartRenders).catch(() => {});
  }

  // A stopped connection must not hold every later cart update indefinitely.
  // Abort only cancels the client wait; it cannot roll back Shopify's mutation.
  async function fetchMutationResponse(url, config) {
    const controller = new AbortController();
    const originalSignal = config.signal;
    const abortFromCaller = () => controller.abort(originalSignal.reason);
    if (originalSignal) {
      if (originalSignal.aborted) abortFromCaller();
      else originalSignal.addEventListener('abort', abortFromCaller, { once: true });
    }
    let timeoutId;
    const deadline = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        const error = new Error('We could not confirm the cart update. Please review your cart before trying again.');
        error.cartTimedOut = true;
        // Settle the deadline first so an AbortError cannot hide the recovery reason.
        reject(error);
        controller.abort();
      }, 30000);
    });
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, { ...config, signal: controller.signal });
          let data;
          try { data = await response.json(); }
          catch (_) { throw new Error('We could not confirm the cart update. Please check your cart before trying again.'); }
          return { response, data };
        })(),
        deadline,
      ]);
    } finally {
      clearTimeout(timeoutId);
      if (originalSignal) originalSignal.removeEventListener('abort', abortFromCaller);
    }
  }

  function mutate(url, config, eventName, extra) {
    const operation = async () => {
      if (recovering) {
        const error = new Error('Your cart is being refreshed. Please wait.');
        error.cartRecovering = true;
        throw error;
      }
      let response, data;
      try { ({ response, data } = await fetchMutationResponse(url, config)); }
      catch (error) {
        if (error && error.cartTimedOut) {
          if (typeof window.liveRegion === 'function') window.liveRegion(error.message);
          recover(error);
          error.cartRecovering = true;
        }
        throw error;
      }
      if (!data || typeof data !== 'object') {
        throw new Error('We could not confirm the cart update. Please check your cart before trying again.');
      }
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || 'The cart update did not go through. Please try again.');
        // Discounts can change line keys while an earlier add is pending.
        // On an invalid/stale change request, show Shopify's current cart without replaying it.
        if (eventName === 'shapes:modalcart:cartqtychange' && response.status === 400) {
          recover(error);
          error.cartRecovering = true;
        }
        throw error;
      }
      if (eventName) await publish(data, eventName, extra);
      return data;
    };
    pending += 1;
    notifyPending();
    const result = queue.then(operation).finally(() => {
      pending -= 1;
      notifyPending();
    });
    queue = result.catch(() => {});
    return result;
  }

  function errorMessage(error) {
    return error && error.name !== 'TypeError' && error.message
      ? error.message
      : 'We could not confirm the cart update. Please check your cart before trying again.';
  }

  return { sections, validate, renderEvent, publish, mutate, recover, errorMessage, get pending() { return pending; } };
})();
